import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { createPhoneValidator, PHONE_FORMATS_BY_DIAL_CODE, significantDigitCount } from '@eudonet/opal';
/**
 * Fallback context used only when the caller does not inject the module
 * "Contexte par défaut" setting (see {@link UseAddPhoneFormOptions.defaultContext}).
 * The US-07 acceptance criteria fall back to Professional, same as US-06.
 */
const DEFAULT_PHONE_CONTEXT = 'Professional';
/** Mobile is the default Type per the Figma "Type" selector (US-07). */
const DEFAULT_PHONE_SUBTYPE = 'Mobile';
/**
 * Collapses a (dial code, national number) pair to the canonical stored form: strict E.164
 * (`+{dialCodeDigits}{significantDigits}`, no trunk zero, no separators). OP-7665's storage
 * decision supersedes the earlier "keep the raw typed form" behaviour — the single leading trunk
 * zero (French `06…`) is stripped here via OPAL's `significantDigitCount`, mirroring OPHUB's own
 * `PhoneNumberNormalizer` and the opdon/opink `StripRedundantTrunkZero` normalizers, so the wire
 * value this composable sends is already E.164 rather than relying on a downstream normalizer to
 * fix it up. The trunk zero is re-derived for display/edit purely from the stored E.164 + country
 * (see `withHeuristicTrunkZero`, `formatPhoneForDisplay`), never persisted separately. Returns
 * `''` when no significant digit remains, so an empty/all-zero entry is left untouched for the
 * validator to flag rather than coerced into a bare `+33`.
 */
export function toStoredPhoneValue(dialCode, nationalNumber) {
    const national = nationalNumber.replace(/\D/g, '');
    if (!national || !/[1-9]/.test(national))
        return '';
    const hasTrunkZero = PHONE_FORMATS_BY_DIAL_CODE[dialCode]?.hasTrunkZero ?? false;
    const significant = significantDigitCount(national, hasTrunkZero);
    return `+${dialCode.replace(/\D/g, '')}${significant}`;
}
/**
 * Form state + validation for the reusable "Ajouter un numéro" modal
 * ({@link AddPhoneModal}). Owner-agnostic: the persistence call and the context
 * mode are injected so the same form drives contact and (future) organisation
 * channels. Mirrors {@link useAddEmailForm}; phone validity reuses the OPAL
 * per-country digit validator instead of an email regex.
 */
export function useAddPhoneForm(options) {
    const { hasPrimaryPhone, withContext, i18nPrefix, defaultContext } = options;
    const { t } = useI18n();
    const tk = (key) => t(`${i18nPrefix.value}.${key}`);
    // Resolves the initial/reset context from the injected module setting, falling
    // back to Professional until the setting-read story wires the real value.
    const initialContext = () => defaultContext?.value ?? DEFAULT_PHONE_CONTEXT;
    const form = reactive({
        context: initialContext(),
        phone: '',
        subType: DEFAULT_PHONE_SUBTYPE,
        organisationId: '',
        isPrimary: false,
    });
    // Mirrors OPAL PhoneInput's internal split (dial code + national digits). Seeded
    // from the `country-change` event the modal forwards on every keystroke.
    const phoneMeta = reactive({ dialCode: '', nationalNumber: '' });
    const organisationIdError = ref('');
    const isProfessional = computed(() => withContext.value && form.context === 'Professional');
    /**
     * National-significant digits, for VALIDATION counting only. Delegates to OPAL's
     * `significantDigitCount`, the single source of truth shared with PhoneInput's own
     * validation, {@link toStoredPhoneValue}, and every other add/edit surface.
     */
    const significantNationalDigits = computed(() => significantDigitCount(phoneMeta.nationalNumber.replace(/\D/g, ''), PHONE_FORMATS_BY_DIAL_CODE[phoneMeta.dialCode]?.hasTrunkZero ?? false));
    // Per-country digit validator (FR = exactly 9 digits, …). Returns the localized
    // digit message via the shared `errors.phoneDigits` key, or undefined when the
    // count matches / the field is empty. Required-ness is handled separately below.
    // CONSTRAINT: pass the RAW digits, not `significantNationalDigits` — createPhoneValidator
    // applies its own single significantDigitCount strip internally. Passing an
    // already-stripped value here double-strips: a normal single-trunk-zero entry is
    // unaffected (idempotent), but an over-typed extra leading zero (e.g. FR "00612345678")
    // would silently validate as 9 digits instead of correctly failing at 10.
    const phoneDigitError = createPhoneValidator(() => phoneMeta.nationalNumber.replace(/\D/g, ''), () => phoneMeta.dialCode, (params) => t('errors.phoneDigits', params));
    /** True once the user has typed at least one significant national digit. */
    const hasNationalDigits = computed(() => significantNationalDigits.value.length > 0);
    /** True when a non-empty number passes the per-country digit-count check. */
    const isPhoneValid = computed(() => hasNationalDigits.value && !phoneDigitError(null));
    // Clear the organisation error as the user corrects it. The phone field error is
    // a `touched`-gated computed owned by the modal (so OPAL PhoneInput's internal
    // English message never wins), so it needs no watcher here.
    watch(() => form.organisationId, (val) => { if (val)
        organisationIdError.value = ''; });
    // Switching back to Personal drops the organisation anchor + its error.
    watch(() => form.context, (ctx) => {
        if (ctx === 'Personal') {
            form.organisationId = '';
            organisationIdError.value = '';
        }
    });
    /** Localized validation message for the phone field, or '' when it is valid. */
    function phoneValidationMessage() {
        if (!hasNationalDigits.value)
            return tk('validation.phoneRequired');
        return phoneDigitError(null) ?? '';
    }
    function validate() {
        let valid = isPhoneValid.value;
        if (isProfessional.value && !form.organisationId) {
            organisationIdError.value = tk('validation.organisationRequired');
            valid = false;
        }
        return valid;
    }
    /**
     * Submit is enabled only when the phone is valid (non-empty, correct digit
     * count) and, in Professional context, an organisation is selected — matching
     * the email modal's "Enregistrer désactivé tant que le champ est invalide".
     */
    const canSubmit = computed(() => {
        if (!isPhoneValid.value)
            return false;
        if (isProfessional.value && !form.organisationId)
            return false;
        return true;
    });
    /** First phone is implicitly primary; otherwise honour the toggle. */
    const effectiveIsPrimary = computed(() => hasPrimaryPhone.value ? form.isPrimary : true);
    /**
     * Collapses separator noise on blur ("à la sortie du champ") — NEVER the trunk zero the user
     * typed. `form.phone` is PhoneInput's v-model: writing the E.164-stripped form back into it
     * made PhoneInput's own modelValue watcher detect a mismatch and re-parse, visibly dropping the
     * "0" from the field the instant the user tabbed out — a regression against OP-7665 AC3 (the
     * field must keep showing exactly what was typed, 0 or not, for the whole editing session, not
     * just live keystrokes). Stripping to E.164 for the WIRE value is `buildPayload`'s job alone,
     * via {@link toStoredPhoneValue}. No-op while the national part is empty so an untouched field
     * stays blank.
     */
    function normalizePhone() {
        const digits = phoneMeta.nationalNumber.replace(/\D/g, '');
        if (digits && /[1-9]/.test(digits)) {
            form.phone = `+${phoneMeta.dialCode.replace(/\D/g, '')}${digits}`;
        }
    }
    /** Builds the owner-agnostic payload from the current form state. */
    function buildPayload() {
        const payload = {
            // Defense-in-depth: re-normalize in case submit fires without a blur
            // (e.g. Enter key) — falls back to the raw value only if meta is unset.
            value: toStoredPhoneValue(phoneMeta.dialCode, phoneMeta.nationalNumber) || form.phone.trim(),
            subType: form.subType,
            isPrimary: effectiveIsPrimary.value,
        };
        if (withContext.value) {
            payload.usage = form.context;
            payload.linkedOrganisationId = isProfessional.value ? form.organisationId || null : null;
        }
        return payload;
    }
    /** Updates the dial code / national number from PhoneInput's `country-change`. */
    function setPhoneMeta(dialCode, nationalNumber) {
        phoneMeta.dialCode = dialCode;
        phoneMeta.nationalNumber = nationalNumber;
    }
    function reset() {
        form.context = initialContext();
        form.phone = '';
        form.subType = DEFAULT_PHONE_SUBTYPE;
        form.organisationId = '';
        form.isPrimary = false;
        phoneMeta.dialCode = '';
        phoneMeta.nationalNumber = '';
        organisationIdError.value = '';
    }
    return {
        form,
        organisationIdError,
        isProfessional,
        isPhoneValid,
        canSubmit,
        effectiveIsPrimary,
        setPhoneMeta,
        phoneValidationMessage,
        normalizePhone,
        validate,
        buildPayload,
        reset,
    };
}
